import type Lenis from "lenis";

// Lenis is created inside ScrollProvider, but the booking provider and the
// floating action bar are mounted outside it and still have to move the page.
// Without a shared handle they fall back to window.scrollTo, which fights the
// smooth-scroll loop instead of riding it.
let instance: Lenis | null = null;

export const registerLenis = (lenis: Lenis | null) => {
  instance = lenis;
};

export const getLenis = () => instance;

// The header overlaps the top of a section on large screens; on phones the bar
// sits at the bottom, so a small nudge is enough.
export const sectionOffset = () =>
  typeof window !== "undefined" && window.innerWidth >= 1024 ? -100 : -20;

const SETTLE_QUIET_MS = 200; // how long the height must hold still
const SETTLE_MAX_MS = 1500; // …and how long we are willing to wait for that
const CORRECTION_DELAY_MS = 1400; // just past the end of the 1.2s tween
const DRIFT_TOLERANCE = 4; // px

const USER_INPUT = ["wheel", "touchstart", "keydown"] as const;

/**
 * Runs `then` once the document has stopped growing.
 *
 * A cross-page jump to `/#book-session` used to fire on a fixed 300ms timer,
 * long before the images above the target had decoded. Every section above it
 * was still short, so the scroll landed on the case-study block and the page
 * then grew underneath the visitor. Measuring the height instead of guessing a
 * delay is what keeps the landing on the right section.
 */
const whenLayoutSettles = (then: () => void) => {
  const start = performance.now();
  let lastHeight = document.body.scrollHeight;
  let quietFor = 0;

  const tick = (previous: number) => {
    const now = performance.now();
    const height = document.body.scrollHeight;

    if (height === lastHeight) {
      quietFor += now - previous;
    } else {
      quietFor = 0;
      lastHeight = height;
    }

    if (quietFor >= SETTLE_QUIET_MS || now - start >= SETTLE_MAX_MS) {
      then();
      return;
    }
    requestAnimationFrame(() => tick(now));
  };

  requestAnimationFrame(() => tick(performance.now()));
};

const land = (el: HTMLElement, immediate: boolean) => {
  const lenis = instance;
  if (lenis) {
    // Lenis caches the document height; a stale one puts the target in the
    // wrong place just as surely as a stale layout does.
    lenis.resize();
    lenis.scrollTo(el, {
      offset: sectionOffset(),
      duration: 1.2,
      immediate,
    });
    return;
  }
  window.scrollTo({
    top: el.getBoundingClientRect().top + window.scrollY + sectionOffset(),
    behavior: immediate ? "auto" : "smooth",
  });
};

/**
 * Scrolls to a section by id, waiting for the page to stop reflowing first and
 * correcting once afterwards if it moved anyway. Returns false if the section
 * is not on this page, which is the caller's cue to navigate instead.
 */
export const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return false;

  let touched = false;
  const markTouched = () => {
    touched = true;
  };

  const go = () => {
    land(el, false);

    // One late pass for anything that only settled while the tween was running.
    // Skipped the moment the visitor takes over — nothing is worse than the page
    // yanking itself out from under a scroll they started.
    USER_INPUT.forEach((event) =>
      window.addEventListener(event, markTouched, { passive: true, once: true }),
    );

    window.setTimeout(() => {
      USER_INPUT.forEach((event) =>
        window.removeEventListener(event, markTouched),
      );
      if (touched) return;
      const drift = el.getBoundingClientRect().top - sectionOffset();
      if (Math.abs(drift) > DRIFT_TOLERANCE) land(el, true);
    }, CORRECTION_DELAY_MS);
  };

  whenLayoutSettles(go);
  return true;
};

/** Top of the page, through lenis when it is in reach. */
export const scrollToTop = () => {
  const lenis = instance;
  if (lenis) lenis.scrollTo(0, { duration: 1.5 });
  else window.scrollTo({ top: 0, behavior: "smooth" });
};
